import isGroupItem from './isGroupItem';
import isInJunkFolder from './isInJunkFolder';
import { CommandBarItem } from './schema/CommandBarItem';
import { ItemActionsProps } from './schema/ItemActionsProps';
import { itemHeaderUnflag } from '../components/ItemActions.locstring.json';
import { itemHeaderMarkAsNotJunk } from '../components/ItemActions.locstring.json';
import onCreateRule from '../handlers/onCreateRule';
import onDelete from '../handlers/onDelete';
import onForward from '../handlers/onForward';
import onPopout from '../handlers/onPopout';
import onPrint from '../handlers/onPrint';
import onReply from '../handlers/onReply';
import onReplyAll from '../handlers/onReplyAll';
import onReplyAllByIm from '../handlers/onReplyAllByIm';
import onReplyByIm from '../handlers/onReplyByIm';
import onReplyByMeeting from '../handlers/onReplyByMeeting';
import onToggleFlagMenuItem from '../handlers/onToggleFlagMenuItem';
import onToggleJunkNotJunk from '../handlers/onToggleJunkNotJunk';
import onToggleReadMenuItem from '../handlers/onToggleReadMenuItem';
import { ControlIcons } from 'owa-control-icons';
import { isFolderOrSubFolderOfMsgRoot } from 'owa-folders';
import loc from 'owa-localize';
import { itemHeaderFlag } from 'owa-locstrings/lib/strings/itemheaderflag.locstring.json';
import { itemHeaderMarkAsJunk } from 'owa-locstrings/lib/strings/itemheadermarkasjunk.locstring.json';
import { markAsRead } from 'owa-locstrings/lib/strings/markasread.locstring.json';
import { markAsUnread } from 'owa-locstrings/lib/strings/markasunread.locstring.json';
import { isJunkEmailEnabledByAdmin } from 'owa-mail-filterable-menu-behavior';
import isInboxRuleEnabledByAdmin from 'owa-mail-inbox-rules/lib/selectors/isInboxRuleEnabledByAdmin';
import { isItemOfMessageType } from 'owa-mail-list-store';
import checkItemReplyForwardDisabled from 'owa-mail-reading-pane-item-actions/lib/utils/checkItemReplyForwardDisabled';
import getItemRightsManagementRestrictions from 'owa-mail-store/lib/utils/getItemRightsManagementRestrictions';
import { ReadActionKey } from 'owa-outlook-service-options';
import Message from 'owa-service/lib/contract/Message';
import { isSignedIn } from 'owa-skype-for-business';
import isSMIMEItem from 'owa-smime/lib/utils/isSMIMEItem';
import { SurfaceActionItem } from 'owa-surface-actions';
import {
    doesUserHaveSharedFolderPermissionFor,
    MenuItemType,
} from 'owa-mail-filterable-menu-behavior';

export const updateMenuItem: Partial<Record<
    ReadActionKey,
    (props: ItemActionsProps, commandBarItem: CommandBarItem) => void
>> = {
    ['Reply']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        const replyForwardRestrictions = checkItemReplyForwardDisabled(
            props.item,
            props.viewState.isConversationItemPart
        );
        commandBarItem.isDisabled = replyForwardRestrictions[0] /* isReplyDisabled */;
        commandBarItem.clickHandler = onReply(
            props.instrumentationContext,
            props.viewState,
            props.item
        );
    },

    ['ReplyAll']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        const replyForwardRestrictions = checkItemReplyForwardDisabled(
            props.item,
            props.viewState.isConversationItemPart
        );
        commandBarItem.isDisabled = replyForwardRestrictions[1] /* isReplyAllDisabled */;
        commandBarItem.clickHandler = onReplyAll(
            props.instrumentationContext,
            props.viewState,
            props.item
        );
    },

    ['Forward']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        const replyForwardRestrictions = checkItemReplyForwardDisabled(
            props.item,
            props.viewState.isConversationItemPart
        );
        commandBarItem.isDisabled = replyForwardRestrictions[2] /* isForwardDisabled */;
        commandBarItem.clickHandler = onForward(props.item, props.instrumentationContext);
    },

    ['ReplyByMeeting']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        commandBarItem.isDisabled = isSMIMEItem(props.item);
        commandBarItem.clickHandler = onReplyByMeeting(props.item);
    },

    ['OtherReplyActions']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        commandBarItem.clickHandler = stopEventPropagation;
        commandBarItem.subMenuActionKeys = ['ReplyByMeeting', 'ReplyByIM', 'ReplyAllByIM'];
    },

    ['MessageSafety']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        commandBarItem.clickHandler = stopEventPropagation;
        commandBarItem.subMenuActionKeys = ['MarkJunkNotJunk'];
    },

    ['ReplyByIM']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        commandBarItem.clickHandler = onReplyByIm(props.item as Message);
        commandBarItem.isDisabled = !isSignedIn();
    },

    ['ReplyAllByIM']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        commandBarItem.clickHandler = onReplyAllByIm(props.item as Message);
        commandBarItem.isDisabled = !isSignedIn();
    },

    ['MarkJunkNotJunk']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        const message = props.item as Message;
        if (
            isJunkEmailEnabledByAdmin() &&
            isFolderOrSubFolderOfMsgRoot(message.ParentFolderId.Id)
        ) {
            commandBarItem.displayName = isInJunkFolder(message)
                ? loc(itemHeaderMarkAsNotJunk)
                : loc(itemHeaderMarkAsJunk); // Override default string based on actual state
            commandBarItem.datapointName = isInJunkFolder(message)
                ? 'MarkItemNotjunk'
                : 'MarkItemJunk';
            commandBarItem.clickHandler = onToggleJunkNotJunk(
                props.item,
                props.instrumentationContext
            );
        }
    },

    ['Delete']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        if (!props.isItemAttachment && doesUserHaveSharedFolderPermissionFor(MenuItemType.Delete)) {
            const message: Message = props.item;
            commandBarItem.clickHandler = onDelete(
                props.viewState,
                props.item,
                props.instrumentationContext
            );
            commandBarItem.isDisabled =
                isGroupItem(props.item) &&
                !message.CanDelete; /* Use canDelete property for group messages */
        }
    },

    ['MarkReadUnread']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        /* Temporarily disable for groups popouts */
        if (!(props.isDeeplink && isGroupItem(props.item))) {
            const message: Message = props.item;
            commandBarItem.displayName = message.IsRead ? loc(markAsUnread) : loc(markAsRead);
            commandBarItem.datapointName = message.IsRead ? 'MarkItemUnread' : 'MarkItemRead';
            if (!message.IsRead) {
                commandBarItem.iconName = ControlIcons.Read;
            }
            commandBarItem.clickHandler = onToggleReadMenuItem(
                props.item,
                props.instrumentationContext
            );
        }
    },

    ['FlagUnflag']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        if (doesUserHaveSharedFolderPermissionFor(MenuItemType.Flag)) {
            const message: Message = props.item;
            const isUnflag = !message.Flag || message.Flag.FlagStatus != 'Flagged';
            commandBarItem.displayName = isUnflag ? loc(itemHeaderFlag) : loc(itemHeaderUnflag);
            commandBarItem.datapointName = isUnflag ? 'FlagItem' : 'UnflagItem';
            commandBarItem.clickHandler = onToggleFlagMenuItem(
                props.item,
                props.instrumentationContext
            );
        }
    },

    ['Print']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        const IRMRestrictions = getItemRightsManagementRestrictions(props.item);
        commandBarItem.clickHandler = onPrint(props.item);
        commandBarItem.isDisabled = !IRMRestrictions.PrintAllowed;
    },

    ['View']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        commandBarItem.clickHandler = stopEventPropagation;
        commandBarItem.subMenuActionKeys = ['Popout'];
    },

    ['AdvancedActions']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        commandBarItem.clickHandler = stopEventPropagation;
        commandBarItem.subMenuActionKeys = ['CreateRule'];
    },

    ['Popout']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        commandBarItem.clickHandler = onPopout(props.item, props.viewState);
    },

    ['CreateRule']: (props: ItemActionsProps, commandBarItem: CommandBarItem) => {
        const message = isItemOfMessageType(props.item) ? (props.item as Message) : null;
        if (
            isInboxRuleEnabledByAdmin() &&
            !!message?.From?.Mailbox &&
            isFolderOrSubFolderOfMsgRoot(
                message?.ParentFolderId.Id
            ) /* Checking if the item is from primary mailbox */
        ) {
            commandBarItem.clickHandler = onCreateRule(props.item as Message, props.actionSource);
        }
    },
};

function stopEventPropagation(
    event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
    item?: SurfaceActionItem
) {
    event.stopPropagation();
}
